import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import Icon from '../../../../atoms/Icon'
import FlexRow from '../../../../molecules/FlexRow'
import { LinkedContactRow } from '../shared/LinkedContactDisplay'
import ActivityDetailModal from '../shared/ActivityDetailModal'
import CreateActivityModal from '../shared/CreateActivityModal'
import { formatActivityDate, formatActivityTime } from '../shared/activityDateHelpers'
import useCategoryOptions from './useCategoryOptions'

const LIST_INVALIDATE_KEYS = [[QUERY_KEYS.redtailDomainQuery, 'list-preset']]

function MeetingRow ({ activity, sizes, onClick }) {
  const datePart = formatActivityDate(activity.start_date)
  const timePart = formatActivityTime(activity)

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{ borderBottom: '1px solid var(--color-neutral-200)', padding: sizes.rowPadding, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: 'var(--text-color-subtle)'
        }}
        >
          <Icon name='calendar' customSize='20px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            <LinkedContactRow contacts={activity.linked_contacts} />
          </div>
          <div style={{
            fontSize: sizes.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {activity.subject || 'Untitled Meeting'}
          </div>
        </div>
        <div style={{
          flexShrink: 0,
          textAlign: 'right',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-color-subtle)',
          whiteSpace: 'nowrap'
        }}
        >
          {datePart ? <div>{datePart}</div> : null}
          {timePart ? <div>{timePart}</div> : null}
        </div>
      </div>
    </div>
  )
}

MeetingRow.propTypes = {
  activity: PropTypes.object.isRequired,
  sizes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

function MeetingsContent ({ items, sizes, members, config, createOpen, onCloseCreate, enableEdit, formMode }) {
  const { clientId } = useAppContext()
  const categoryOptions = useCategoryOptions(clientId, config?.categoryIds)
  const [selectedActivity, setSelectedActivity] = useState(null)

  const handleCloseDetail = useCallback(() => setSelectedActivity(null), [])

  return (
    <>
      {!items.length
        ? (
          <FlexRow
            className='justify-center items-center'
            style={{
              minHeight: '80px',
              color: 'var(--text-color-subtle)',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            No upcoming meetings
          </FlexRow>
        )
        : items.map(activity => (
          <MeetingRow
            key={activity.id}
            activity={activity}
            sizes={sizes}
            onClick={() => setSelectedActivity(activity)}
          />
        ))}

      <CreateActivityModal
        open={createOpen}
        onClose={onCloseCreate}
        config={config}
        members={members}
        formMode={formMode}
        createLabel='Create Meeting'
        invalidateQueryKeys={LIST_INVALIDATE_KEYS}
        categoryOptions={categoryOptions}
      />

      <ActivityDetailModal
        open={!!selectedActivity}
        onClose={handleCloseDetail}
        activity={selectedActivity}
        config={config}
        members={members}
        enableEdit={enableEdit}
        formMode={formMode}
        invalidateQueryKeys={LIST_INVALIDATE_KEYS}
        categoryOptions={categoryOptions}
      />
    </>
  )
}

MeetingsContent.propTypes = {
  items: PropTypes.array.isRequired,
  sizes: PropTypes.object.isRequired,
  members: PropTypes.array,
  config: PropTypes.object,
  createOpen: PropTypes.bool,
  onCloseCreate: PropTypes.func,
  enableEdit: PropTypes.bool,
  formMode: PropTypes.string
}

export default MeetingsContent
